// @flow

import React, { Component } from "react"
import { toastr } from "react-redux-toastr"
import { connect } from "react-redux"
import BigList from "../../components/BigList"
import {
  getGroups,
  newGroup,
  getUsers,
  newUser,
  updateUser,
  removeUser,
} from "../../actions"
import style from "./style.css"
import FontIcon from "react-toolbox/lib/font_icon/FontIcon"
import Autocomplete from "react-toolbox/lib/autocomplete/Autocomplete"
import Input from "react-toolbox/lib/input"
import Dialog from "react-toolbox/lib/dialog"
import Division from "../../components/Division"
import * as strings from "../../strings"
import _ from "lodash"

// Email regex validation is difficult, don't try to impose too many
// constraints, it will fail
const EMAIL_REGEX = /^.+@.+$/

const USER_COLUMNS = [
  {
    title: strings.generalStrings.name,
    key: "firstName",
    transform: ( element, key, item ) => element + " " + item[ "lastName" ],
  },
  { title: strings.generalStrings.email, key: "email" },
  {
    title: strings.generalStrings.groups,
    key: "groups",
    transform: groups => groups.join( ", " ),
  },
]

class ManageUsers extends Component {
  state = {
    forms: {},
    formsMetadata: {},
  }
  props: {
    user: any,
    systemUsers: any[],
    systemGroups: any[],
    getGroups: () => {},
    newGroup: any,
    getUsers: () => [],
    newUser: ( any ) => [],
    updateUser: ( any ) => [],
    removeUser: ( number ) => [],
  }

  componentWillMount () {
    if ( this.props.systemUsers.length === 0 ) {
      this.props.getUsers()
    }
    if ( this.props.systemGroups.length === 0 ) {
      this.props.getGroups()
    }
  }

  handleMetadataChange = ( formName: string, name: string ) => value => this.setState( {
    formsMetadata: {
      ...this.state.formsMetadata,
      [ formName ]: {
        ...this.state.formsMetadata[ formName ],
        [ name ]: value,
      },
    },
  } )

  handleToggleVisible = ( formName: string ) => () =>
    this.handleMetadataChange( formName, "visible" )( !(
      this.state.formsMetadata[ formName ] &&
      this.state.formsMetadata[ formName ].visible
    ) )

  handleFormChange = ( formName: string, name: string ) => value => this.setState( {
    forms: {
      ...this.state.forms,
      [ formName ]: {
        ...this.state.forms[ formName ],
        [ name ]: value,
      },
    },
  } )

  resetForm = ( formName: string ) => this.setState( {
    formsMetadata: {
      ...this.state.formsMetadata,
      [ formName ]: {},
    },
    forms: {
      ...this.state.forms,
      [ formName ]: {},
    },
  } )

  registerUser = () => {
    this.handleToggleVisible( "addUserForm" )()

    this.props.newUser( _.omit( this.state.forms.addUserForm, [ "passwordConfirm" ] ) )
      .then(
        () => toastr.success( strings.management.userAdded ),
        () => toastr.error( strings.management.errorAddingUser ),
      )

    this.resetForm( "addUserForm" )
  }

  updateUser = () => {
    this.handleToggleVisible( "editUserForm" )()

    this.props.updateUser( _.omit( this.state.forms.editUserForm, [ "passwordConfirm", "recoveryHash" ] ) )
      .then(
        () => toastr.success( strings.management.userUpdated ),
        () => toastr.error( strings.management.errorUpdatingUser ),
      )

    this.resetForm( "editUserForm" )
  }

  actionRemoveUser = () => {
    this.handleToggleVisible( "editUserForm" )()

    const form = this.state.forms.editUserForm
    const userId = form && form.id

    this.props.removeUser( userId )
      .then(
        () => toastr.success( strings.management.userRemoved ),
        () => toastr.error( strings.management.errorRemovingUser ),
      )
  }

  addUserDialog = () => {
    const formName = "addUserForm"
    const actions = [
      { label: strings.generalStrings.cancel, onClick: this.handleToggleVisible( "addUserForm" ) },
      { label: strings.generalStrings.keep, onClick: this.actionValidateDialog(
        false,
        formName,
        this.validateUserDialog,
        this.registerUser,
      ) },
    ]

    return this.renderUserDialog( actions, strings.management.addUser, formName )
  }

  validateUserDialog = ( isEdit: boolean, formData: any ) => {
    const { firstName, lastName, passwordConfirm, password, email } = formData

    const emailMatchesRegex = EMAIL_REGEX.test( email )
    const emailValid = ( email && email.length > 0 ) && emailMatchesRegex

    const passwordValid = isEdit ?
      password === passwordConfirm :
      ( password && password.length > 0 ) && password === passwordConfirm
    const passwordConfirmValid = passwordValid

    const firstNameValid = firstName && firstName.length > 0
    const lastNameValid = lastName && lastName.length > 0

    return {
      firstNameValid,
      lastNameValid,
      emailValid,
      passwordValid,
      passwordConfirmValid,
    }
  }

  actionValidateDialog = (
    isEdit: boolean,
    formName: string,
    validationFn: ( boolean, any ) => boolean,
    action: () => any,
  ) => ( ...args ) => {
    const formData = this.state.forms[ formName ] || {}

    const inputValidation = validationFn( isEdit, formData )
    const formOk = Object.values( inputValidation ).every( value => value )

    if ( formOk ) {
      return action( ...args )
    } else {
      this.handleMetadataChange( formName, "showAllErrors" )( true )
    }

    return undefined
  }

  renderUserDialog = ( actions, title, formType ) => {
    const formMetadata = this.state.formsMetadata[ formType ] || {}
    const formData = this.state.forms[ formType ] || {}
    const groupMap = this.props.systemGroups.reduce( ( acc, group ) => ( {
      ...acc,
      [ group.name ]: group.name,
    } ), {} )

    const {
      firstNameValid,
      lastNameValid,
      emailValid,
      passwordValid,
      passwordConfirmValid,
    } = this.validateUserDialog( formType === "editUserForm", formData )

    return (
      <Dialog
        className={style.dialogBox}
        actions={actions}
        active={!!formMetadata.visible}
        onEscKeyDown={this.handleToggleVisible( formType )}
        onOverlayClick={this.handleToggleVisible( formType )}
      >
        <div className={style.dialogContainer}>
          <div className={style.blueBar}>
          </div>
          <Division title={title}/>
          <div className={[ style.addUserRow, style.dialogRow ].join( " " )}>
            <Input
              type="text"
              label={strings.generalStrings.firstName}
              value={formData.firstName}
              onChange={this.handleFormChange( formType, "firstName" )}
              error={( formMetadata.showAllErrors && !firstNameValid ) ? strings.errorMessages.invalid : undefined}
              required={formType !== "editUserForm"}
            />
            <Input
              type="text"
              label={strings.generalStrings.lastName}
              value={formData.lastName}
              onChange={this.handleFormChange( formType, "lastName" )}
              error={( formMetadata.showAllErrors && !lastNameValid ) ? strings.errorMessages.invalid : undefined}
              required={formType !== "editUserForm"}
            />
            <Input
              type="email"
              label={strings.generalStrings.email}
              value={formData.email}
              maxLength={254}
              onChange={this.handleFormChange( formType, "email" )}
              error={( formMetadata.showAllErrors && !emailValid ) ? strings.errorMessages.invalid : undefined}
              required={formType !== "editUserForm"}
              theme={{
                counter: style.hideLengthCounter,
              }}
            />
            <Input
              type="password"
              label={strings.generalStrings.password}
              value={formData.password}
              onChange={this.handleFormChange( formType, "password" )}
              error={( formMetadata.showAllErrors && !passwordValid ) ? strings.errorMessages.invalid : undefined}
              required={formType !== "editUserForm"}
            />
            <Input
              type="password"
              label={strings.generalStrings.confirmPassword}
              value={formData.passwordConfirm}
              onChange={this.handleFormChange( formType, "passwordConfirm" )}
              error={( formMetadata.showAllErrors && !passwordConfirmValid ) ? strings.errorMessages.invalid : undefined}
              required={formType !== "editUserForm"}
            />
            <Autocomplete
              direction="down"
              selectedPosition="above"
              label={strings.generalStrings.groups}
              onChange={this.handleFormChange( formType, "groups" )}
              source={groupMap}
              value={formData.groups}
            />
          </div>
        </div>
      </Dialog>
    )
  }

  registerGroup = () => {
    this.handleToggleVisible( "addGroupForm" )()

    this.props.newGroup( this.state.forms.addGroupForm )
      .then(
        () => toastr.success( strings.management.groupAdded ),
        () => toastr.error( strings.management.errorAddingGroup ),
      )

    this.resetForm( "addGroupForm" )
  }

  validateGroupDialog = ( isEdit: boolean, formData: any ) => {
    const { name } = formData

    const nameUnique = name && !this.props.systemGroups
      .find( group => group.name.toLowerCase() === name.toLowerCase() )

    const nameValid = name && name.length > 0 && nameUnique

    return {
      nameValid,
    }
  }


  addGroupDialog = () => {
    const formName = "addGroupForm"
    const formMetadata = this.state.formsMetadata.addGroupForm || {}
    const formData = this.state.forms.addGroupForm || {}
    const actions = [
      { label: strings.generalStrings.cancel, onClick: this.handleToggleVisible( formName ) },
      { label: strings.generalStrings.keep, onClick: this.actionValidateDialog(
        false,
        formName,
        this.validateGroupDialog,
        this.registerGroup,
      ) },
    ]

    const { nameValid } = this.validateGroupDialog( false, formData )

    return (
      <Dialog
        className={style.dialogBox}
        actions={actions}
        active={!!formMetadata.visible}
        onEscKeyDown={this.handleToggleVisible( formName )}
        onOverlayClick={this.handleToggleVisible( formName )}
      >
        <div className={style.dialogContainer}>
          <div className={style.blueBar}>
          </div>
          <Division title={strings.management.addGroup}/>
          <div className={[ style.addUserRow, style.dialogRow ].join( " " )}>
            <Input
              type="text"
              label={strings.generalStrings.name}
              value={formData.name}
              onChange={this.handleFormChange( formName, "name" )}
              error={( formMetadata.showAllErrors && !nameValid ) ? strings.errorMessages.invalid : undefined}
              required
            />
          </div>
        </div>
      </Dialog>
    )
  }

  renderToolbar () {
    return (
      <div className={style.toolbar}>
        <div
          className={style.toolbarButton}
          onClick={this.handleToggleVisible( "addUserForm" )}
          onKeyPress={this.handleToggleVisible( "addUserForm" )}
          role="button"
          tabIndex="0"
        >
          <FontIcon value="add" /> {strings.management.addUser}
        </div>
        <div
          className={style.toolbarButton}
          onClick={this.handleToggleVisible( "addGroupForm" )}
          onKeyPress={this.handleToggleVisible( "addGroupForm" )}
          role="button"
          tabIndex="0"
        >
          <FontIcon value="add" /> {strings.management.addGroup}
        </div>
      </div>
    )
  }

  editUserDialog = () => {
    const formName = "editUserForm"
    const actions = [
      { label: strings.generalStrings.delete, onClick: this.actionRemoveUser },
      { label: strings.generalStrings.cancel, onClick: this.handleToggleVisible( formName ) },
      { label: strings.generalStrings.keep, onClick: this.actionValidateDialog(
        true,
        formName,
        this.validateUserDialog,
        this.updateUser,
      ) },
    ]

    return this.renderUserDialog( actions, strings.management.editUser, formName )
  }

  render () {
    if ( !this.props.systemUsers ) {
      return ( <div /> )
    }

    return (
      <div className={style.root}>
        {this.renderToolbar()}
        {this.addUserDialog()}
        {this.addGroupDialog()}
        {this.editUserDialog()}
        <BigList
          loadMore={() => {}}
          hasMoreItems={() => false}
          items={this.props.systemUsers.map( user => {
            return {
              ...user,
              _onClick: () => {
                this.setState( {
                  forms: {
                    ...this.state.forms,
                    editUserForm: { ...user },
                  },
                } )

                this.handleToggleVisible( "editUserForm" )()
              },
            }
          } ) }
          columns={USER_COLUMNS}
        />
      </div>
    )
  }
}

const mapStateToProps = store => {
  return {
    systemGroups: store.systemGroups,
    systemUsers: store.systemUsers,
    user: store.user,
  }
}

export default connect( mapStateToProps, {
  getGroups,
  newGroup,
  getUsers,
  newUser,
  updateUser,
  removeUser,
} )( ManageUsers )



// WEBPACK FOOTER //
// ./src/views/ManageUsers/index.jsx